import { IonIcon } from "@ionic/react";
import { heart, heartOutline } from "ionicons/icons";
import { AnimatePresence, motion } from "motion/react";
import React, { useState } from "react";
import { useReducedMotion } from "../../../hooks/useReducedMotion.hook";
import { getUIColorPalette } from "../../../theme/ColorPalette";
import { isUiMotionEnabled, resolveUiSpring } from "../../shared/motion/uiMotion.tools";

/** Visual treatment for the save toggle in the itinerary panel chrome row. */
type ItineraryDetailsSaveToggleVariant = "default" | "embedded";

interface ItineraryDetailsSaveToggleProps {

    /** Whether dark overlay palette tokens should be used. */
    "isDarkUi": boolean;

    /** Whether the selected itinerary is already saved. */
    "isSaved": boolean;

    /** Persists or removes the saved itinerary. */
    "onToggle": () => void;

    /**
     * `default` — standalone circular button.
     * `embedded` — right segment of the unified timing/save capsule (no outer ring).
     */
    "variant"?: ItineraryDetailsSaveToggleVariant;
}

/**
 * Saved-itinerary toggle for the itinerary panel chrome row.
 *
 * @remarks In `embedded` mode the control sits flush on the right of the timing capsule, separated
 * by a divider, so it stays away from the back button while reading as one module with the itinerary
 * duration.
 */
const ItineraryDetailsSaveToggle: React.FC<ItineraryDetailsSaveToggleProps> = ( {
    isDarkUi,
    isSaved,
    onToggle,
    variant = "default",
} ) => {
    const palette = getUIColorPalette( isDarkUi );
    const reducedMotion = useReducedMotion();
    const animationsEnabled = isUiMotionEnabled( reducedMotion );
    const [ previousIsSaved, setPreviousIsSaved ] = useState( isSaved );
    const [ pingKey, setPingKey ] = useState( 0 );
    const isEmbedded = variant === "embedded";

    /*
     * Derives the one-shot "ping" trigger during render (React-endorsed pattern for adjusting
     * state on prop change) instead of a `useEffect`, which would fire the halo animation one
     * frame late and trigger `react-hooks/set-state-in-effect`.
     */
    if ( isSaved !== previousIsSaved ) {
        setPreviousIsSaved( isSaved );
        if ( isSaved ) {
            setPingKey( ( key ) => key + 1 );
        }
    }

    const buttonStyle: React.CSSProperties = isEmbedded
        ? {
            "background": isSaved ? `${ palette.brandPrimary }2e` : "transparent",
            "color":      isSaved ? palette.brandPrimary : palette.overlayTextSecondary,
        }
        : {
            "borderColor": isSaved ? palette.brandPrimary : palette.overlayBorderStrong,
            "background":  isSaved ? `${ palette.brandPrimary }1f` : "transparent",
            "color":       isSaved ? palette.brandPrimary : palette.overlayTextSecondary,
        };

    const embeddedIconClassName = "pointer-events-none block h-4 w-4 shrink-0 -translate-x-px";

    const buttonClassName = isEmbedded
        ? "relative z-10 inline-flex h-9 w-11 shrink-0 items-center justify-center self-center transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset"
        : "relative z-10 inline-flex h-9 w-9 shrink-0 items-center justify-center overflow-visible rounded-full border-2 border-solid transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1";

    return (
        <button
            aria-label={isSaved ? "Retirer l'itinéraire des favoris" : "Enregistrer l'itinéraire"}
            aria-pressed={isSaved}
            className={buttonClassName}
            id="itinerary-details-save-toggle"
            onClick={onToggle}
            style={buttonStyle}
            type="button"
        >
            {animationsEnabled && pingKey > 0
                ? (
                    <motion.span
                        animate={{
                            "opacity": 0,
                            "scale":   1.9,
                        }}
                        className={`pointer-events-none absolute inset-0 ${ isEmbedded ? "" : "rounded-full" }`}
                        initial={{
                            "opacity": 0.5,
                            "scale":   0.6,
                        }}
                        key={pingKey}
                        style={{ "backgroundColor": palette.brandPrimary }}
                        transition={{
                            "duration": 0.5,
                            "ease":     "easeOut",
                        }}
                    />
                )
                : null}
            {animationsEnabled
                ? (
                    <AnimatePresence
                        initial={false}
                        mode="wait"
                    >
                        <motion.span
                            animate={{
                                "opacity": 1,
                                "scale":   1,
                            }}
                            className="inline-flex items-center justify-center leading-none"
                            exit={{
                                "opacity": 0,
                                "scale":   0.5,
                            }}
                            initial={{
                                "opacity": 0,
                                "scale":   0.5,
                            }}
                            key={isSaved ? "saved" : "idle"}
                            transition={resolveUiSpring( reducedMotion, "default" )}
                        >
                            <IonIcon
                                aria-hidden={true}
                                className={isEmbedded ? embeddedIconClassName : "h-[18px] w-[18px]"}
                                icon={isSaved ? heart : heartOutline}
                            />
                        </motion.span>
                    </AnimatePresence>
                )
                : (
                    <IonIcon
                        aria-hidden={true}
                        className={isEmbedded ? embeddedIconClassName : "h-[18px] w-[18px]"}
                        icon={isSaved ? heart : heartOutline}
                    />
                )}
        </button>
    );
};

export default ItineraryDetailsSaveToggle;
